/* ============================================================
   Framing options — visual proxy layer for the default product page.

   Splits the single backend "Frame" option (Print Only / Natural / White /
   Black / Natural Box / White Box / Black Box) into two visual controls:
     - Framing colour  (Print Only / Natural / White / Black)
     - Framing type    (Classic / Premium Shadow Box)  — "Box" = Premium
   and renders a dimension-aware size list (Size — dimensions — price), where
   dimensions = print size + frame border and update with the selected frame.

   The native Size / Frame <select>s remain the source of truth: this layer
   reflects clicks onto them via a dispatched native 'change', so framing.js,
   the zero-price variant filter and the cart all keep working untouched.
   It never resolves the cart variant or touches the frame preview directly.
   ============================================================ */
(function () {
  // ISO paper sizes in millimetres, portrait (short × long).
  // Only used as a fallback when a size has no entry in DIMS below.
  var PAPER_MM = {
    A0: [841, 1189], A1: [594, 841], A2: [420, 594], A3: [297, 420],
    A4: [210, 297], A5: [148, 210], A6: [105, 148],
    B0: [1000, 1414], B1: [707, 1000], B2: [500, 707], B3: [353, 500]
  };

  // Authoritative finished sizes per size code, keyed by the paper/inch token
  // found in the variant's Size value (A4, B1, 12x12, 40x60 …).
  //   unframed = print/paper size (W × H)
  //   classic  = Classic frame outer size (W × H × depth)
  //   box      = Premium Shadow Box outer size (W × H × depth)
  // Each state carries both cm and in strings (US market shows inches).
  // null = "Not Available" / "TBC" for that size+type (option hides anyway).
  var DIMS = {
    'A4':    { unframed: { cm: '21 × 29.7 cm',    in: '8.3 × 11.7 in' },   classic: { cm: '24 × 33 × 2 cm',      in: '9.4 × 13 × 0.8 in' },     box: { cm: '24 × 33 × 3 cm',      in: '9.4 × 13 × 1.2 in' } },
    'A3':    { unframed: { cm: '29.7 × 42 cm',    in: '11.7 × 16.5 in' },  classic: { cm: '33 × 45.4 × 2 cm',    in: '13 × 17.9 × 0.8 in' },    box: { cm: '33 × 45.4 × 3 cm',    in: '13 × 17.9 × 1.2 in' } },
    'A2':    { unframed: { cm: '42 × 59.4 cm',    in: '16.5 × 23.4 in' },  classic: { cm: '45 × 62.4 × 2 cm',    in: '17.7 × 24.6 × 0.8 in' },  box: { cm: '46 × 63.5 × 4.5 cm',  in: '18.1 × 25 × 1.8 in' } },
    'A1':    { unframed: { cm: '59.4 × 84.1 cm',  in: '23.4 × 33.1 in' },  classic: { cm: '62.4 × 87.1 × 2 cm',  in: '24.6 × 34.3 × 0.8 in' },  box: { cm: '63.5 × 88 × 4.5 cm',  in: '25 × 34.6 × 1.8 in' } },
    'B1':    { unframed: { cm: '70.7 × 100 cm',   in: '27.8 × 39.4 in' },  classic: { cm: '73.7 × 103 × 2 cm',   in: '29 × 40.6 × 0.8 in' },    box: { cm: '75 × 105 × 4.5 cm',   in: '29.5 × 41.3 × 1.8 in' } },
    'A0':    { unframed: { cm: '84.1 × 118.9 cm', in: '33.1 × 46.8 in' },  classic: { cm: '87.2 × 122.1 × 3 cm', in: '34.3 × 48.1 × 1.2 in' },  box: { cm: '89 × 124 × 4.5 cm',   in: '35 × 48.8 × 1.8 in' } },
    '40x60': { unframed: { cm: '101.6 × 152.4 cm', in: '40 × 60 in' },     classic: null,                                                      box: { cm: '106 × 157 × 4.5 cm',  in: '41.7 × 61.8 × 1.8 in' } },
    '50x75': { unframed: { cm: '127 × 190.5 cm',  in: '50 × 75 in' },      classic: null,                                                      box: null },
    '60x90': { unframed: { cm: '152.4 × 228.6 cm', in: '60 × 90 in' },     classic: null,                                                      box: null },
    '12x12': { unframed: { cm: '30.5 × 30.5 cm',  in: '12 × 12 in' },  classic: { cm: '33.5 × 33.5 × 2 cm',  in: '13.2 × 13.2 × 0.8 in' },  box: { cm: '33.5 × 33.5 × 3 cm',  in: '13.2 × 13.2 × 1.2 in' } },
    '20x20': { unframed: { cm: '50.8 × 50.8 cm',  in: '20 × 20 in' },  classic: { cm: '54 × 54 × 2 cm',      in: '21.3 × 21.3 × 0.8 in' },  box: { cm: '58 × 58 × 4.5 cm',    in: '22.8 × 22.8 × 1.8 in' } },
    '28x28': { unframed: { cm: '71.1 × 71.1 cm',  in: '28 × 28 in' },  classic: { cm: '74 × 74 × 2 cm',      in: '29.1 × 29.1 × 0.8 in' },  box: { cm: '78 × 78 × 4.5 cm',    in: '30.7 × 30.7 × 1.8 in' } },
    '36x36': { unframed: { cm: '91.4 × 91.4 cm',  in: '36 × 36 in' },      classic: { cm: '94.5 × 94.5 × 3 cm',  in: '37.2 × 37.2 × 1.2 in' },  box: { cm: '96 × 96 × 3 cm',      in: '37.8 × 37.8 × 1.2 in' } },
    '45x45': { unframed: { cm: '114.3 × 114.3 cm', in: '45 × 45 in' },     classic: null,                                                      box: { cm: '118 × 118 × 4.5 cm',  in: '46.5 × 46.5 × 1.8 in' } }
  };

  function init(root) {
    var fo = root.querySelector('.fo-variants');
    var jsonEl = root.querySelector('[data-product-json]');
    if (!fo || !jsonEl || fo.__foInit) return;

    // Build marker — lets you confirm the deployed asset is current:
    // open the browser console and type  window.__foVersion
    window.__foVersion = '2026-07-05-settle';
    fo.setAttribute('data-fo-version', window.__foVersion);

    var data;
    try { data = JSON.parse(jsonEl.textContent); } catch (e) { return; }

    var product = data.product || {};
    var variants = (product.variants || []).filter(function (v) { return Number(v.price) > 0; });
    if (!variants.length) return;
    fo.__foInit = true;

    var sizePos = parseInt(fo.getAttribute('data-fo-size-pos'), 10) || 0;
    var framePos = parseInt(fo.getAttribute('data-fo-frame-pos'), 10) || 0;
    var classicBorder = parseFloat(fo.getAttribute('data-fo-classic-border')) || 0;
    var boxBorder = parseFloat(fo.getAttribute('data-fo-box-border')) || 0;
    var unit = fo.getAttribute('data-fo-unit') === 'in' ? 'in' : 'cm';
    var orientation = fo.getAttribute('data-fo-orientation') === 'H' ? 'H' : 'V';
    var moneyFormat = fo.getAttribute('data-fo-money-format') || '${{amount}}';
    var classicLabel = fo.getAttribute('data-fo-classic-label') || 'Classic';
    var premiumLabel = fo.getAttribute('data-fo-premium-label') || 'Premium Shadow Box';

    var sizeSelect = root.querySelector('.product-form__option.Size select, select[data-select-name="Size"]');
    var frameSelect = root.querySelector('.product-form__option.Frame select, select[data-select-name="Frame"]');
    if (!frameSelect) return;

    var frameEl = fo.querySelector('[data-fo-frame]');
    var typeEl = fo.querySelector('[data-fo-type]');
    var auNoteEl = root.querySelector('[data-fo-au-note]');
    var sizeRowsEl = fo.querySelector('[data-fo-size-rows]');
    var swatchesEl = fo.querySelector('[data-fo-frame-swatches]');
    var typeBtnsEl = fo.querySelector('[data-fo-type-buttons]');
    var frameColorLabel = fo.querySelector('[data-fo-frame-color]');
    var typeValueLabel = fo.querySelector('[data-fo-type-value]');
    var sizeValueEl = fo.querySelector('[data-fo-size-value]');

    var mqMobile = window.matchMedia('(max-width: 640px)');
    function isMobile() { return mqMobile.matches; }

    // ---- helpers -------------------------------------------------------------
    function optKey(pos) { return 'option' + pos; }

    function findVariant(sizeVal, frameVal) {
      return variants.filter(function (v) {
        if (sizePos && sizeVal != null && v[optKey(sizePos)] !== sizeVal) return false;
        if (framePos && frameVal != null && v[optKey(framePos)] !== frameVal) return false;
        return true;
      })[0];
    }

    function stripHtml(s) {
      if (s == null) return '';
      var d = document.createElement('div');
      d.innerHTML = String(s);
      return (d.textContent || d.innerText || '').replace(/\s+/g, ' ').trim();
    }

    function formatMoney(cents) {
      var out;
      if (window.Shopify && typeof window.Shopify.formatMoney === 'function') {
        out = window.Shopify.formatMoney(cents, moneyFormat);
      } else {
        var value = (cents / 100).toFixed(0).replace(/\B(?=(\d{3})+(?!\d))/g, ',');
        out = moneyFormat.replace(/\{\{\s*amount[^}]*\}\}/, value);
      }
      // money_format often wraps the value in <span class="money">…</span>;
      // strip markup so it renders as plain text in our controls.
      return stripHtml(out);
    }

    function parseFrame(v) {
      var s = (v == null ? '' : String(v)).trim();
      var printOnly = /print\s*only/i.test(s) || /^none$/i.test(s) || /^no\s*frame/i.test(s);
      var box = /\bbox\b/i.test(s);
      var color = s.replace(/\bbox\b/i, '').replace(/\s{2,}/g, ' ').trim();
      return {
        value: s,
        color: printOnly ? s : color,
        type: printOnly ? 'none' : (box ? 'premium' : 'classic'),
        printOnly: printOnly
      };
    }

    function chipClass(color, printOnly) {
      if (printOnly) return 'none';
      var c = color.toLowerCase();
      if (c.indexOf('black') >= 0) return 'black';
      if (c.indexOf('white') >= 0) return 'white';
      if (c.indexOf('natural') >= 0 || c.indexOf('oak') >= 0 || c.indexOf('wood') >= 0) return 'natural';
      return 'other';
    }

    // print dimensions in cm for a size value, honouring orientation.
    function printDimsCm(sizeVal) {
      var s = String(sizeVal || '');
      var w, h;
      var m = s.match(/(\d+(?:\.\d+)?)\s*[x×]\s*(\d+(?:\.\d+)?)\s*(cm|mm|inch|in|")?/i);
      if (m) {
        var a = parseFloat(m[1]), b = parseFloat(m[2]);
        var u = (m[3] || 'cm').toLowerCase();
        var factor = u === 'mm' ? 0.1 : (u === 'cm' ? 1 : 2.54); // inch/in/" -> cm
        w = a * factor; h = b * factor;
      } else {
        var tok = s.toUpperCase().match(/\b([AB][0-6])\b/);
        if (tok && PAPER_MM[tok[1]]) {
          w = PAPER_MM[tok[1]][0] / 10;
          h = PAPER_MM[tok[1]][1] / 10;
        } else {
          return null;
        }
      }
      // tables/explicit are short × long (portrait); swap for landscape.
      if (orientation === 'H') { var t = w; w = h; h = t; }
      return { w: w, h: h };
    }

    function borderCmFor(type) {
      if (type === 'premium') return boxBorder;
      if (type === 'classic') return classicBorder;
      return 0; // none / print only
    }

    function fmtNum(v) {
      var r = Math.round(v * 10) / 10;
      return (r % 1 === 0) ? String(r) : r.toFixed(1);
    }

    // Map a frame type to the DIMS state key.
    function dimState(type) {
      return type === 'premium' ? 'box' : (type === 'classic' ? 'classic' : 'unframed');
    }

    // Find the size token (A4 / B1 / 12x12 / 40x60 …) in a Size value and the
    // matching DIMS entry. Only tokens present in DIMS are accepted, which
    // guards against accidentally matching embedded cm dimensions.
    function sizeMatch(value) {
      var s = String(value);
      var iso = s.toUpperCase().match(/\b([AB][0-6])\b/);
      if (iso && DIMS[iso[1]]) return { key: iso[1], iso: true, entry: DIMS[iso[1]] };
      var re = /(\d+(?:\.\d+)?)\s*[x×]\s*(\d+(?:\.\d+)?)/gi, m;
      while ((m = re.exec(s))) {
        var k = m[1] + 'x' + m[2];
        if (DIMS[k]) return { key: k, iso: false, entry: DIMS[k] };
      }
      return null;
    }

    // Fallback dims (print size + universal border) for sizes absent from DIMS.
    function formatDimsFallback(sizeVal, type) {
      var d = printDimsCm(sizeVal);
      if (!d) return '';
      var b = borderCmFor(type);
      var w = d.w + 2 * b;
      var h = d.h + 2 * b;
      if (unit === 'in') return fmtNum(w / 2.54) + ' × ' + fmtNum(h / 2.54) + ' in';
      return fmtNum(w) + ' × ' + fmtNum(h) + ' cm';
    }

    // Authoritative finished dimensions for the selected frame type, in the
    // market unit. Returns '' when the size/type combination has no entry.
    function formatDims(sizeVal, type) {
      var mm = sizeMatch(sizeVal);
      if (!mm) return formatDimsFallback(sizeVal, type);
      var st = mm.entry[dimState(type)];
      if (!st) return '';
      return st[unit] || st.cm || '';
    }

    // Paper code (A4, A3, A0, B1 …) embedded in a size value, if any.
    function sizeCode(value) {
      var m = String(value).toUpperCase().match(/\b([AB][0-6])\b/);
      return m ? m[1] : '';
    }

    // Display name: the size label without its paper code, "-es" junk or dims.
    // "Mini - A4" -> "Mini", "X-Large - B1" -> "X-Large", "Giant -es" -> "Giant".
    function sizeName(value) {
      var s = String(value);
      var code = sizeCode(value);
      if (code) s = s.replace(new RegExp('\\s*-?\\s*' + code + '\\b', 'i'), ' ');
      // Strip dimension tokens (40x60", 30 x 30 cm, (12x12 inch), 21.0x29.7).
      s = s.replace(/\(?\s*\d+(?:\.\d+)?\s*[x×]\s*\d+(?:\.\d+)?\s*(?:cm|mm|inch|in|")?\s*\)?/gi, ' ');
      // Strip the "-es" / " es" junk token: a separator (space/nbsp/zero-width/
      // any hyphen or unicode dash) followed by a standalone "es". ASCII-escaped
      // so the source can never be mangled by encoding; the (?![a-z]) guard
      // protects real words ("Tess", "James"). Handles every size pattern:
      // "Giant -es", "Mini -es - 12x12\"", "Statement -es" -> "Giant"/"Mini"/"Statement".
      s = s.replace(/[\s ​ - \-‐-―]+es(?![a-z])/gi, '');
      // Tidy edges; keep internal hyphens so "X-Large" stays intact.
      s = s.replace(/\s{2,}/g, ' ')
           .replace(/^[\s\-‐-―·|]+/, '')
           .replace(/[\s\-‐-―·|]+$/, '')
           .trim();
      return s || String(value);
    }

    // ---- derive frame data ----------------------------------------------------
    var frameInfos = [];
    (function () {
      var seen = {};
      variants.forEach(function (v) {
        var fv = framePos ? v[optKey(framePos)] : null;
        if (fv != null && !seen[fv]) { seen[fv] = 1; frameInfos.push(parseFrame(fv)); }
      });
    })();

    var preferred = ['print only', 'natural', 'white', 'black'];
    var colors = [];
    (function () {
      var seen = {};
      frameInfos.forEach(function (fi) {
        if (!seen[fi.color]) { seen[fi.color] = 1; colors.push(fi.color); }
      });
      colors.sort(function (a, b) {
        var ia = preferred.indexOf(a.toLowerCase()); if (ia < 0) ia = 99;
        var ib = preferred.indexOf(b.toLowerCase()); if (ib < 0) ib = 99;
        return ia - ib;
      });
    })();

    var hasPremium = frameInfos.some(function (fi) { return fi.type === 'premium'; });
    var hasClassic = frameInfos.some(function (fi) { return fi.type === 'classic'; });

    function isPrintOnlyColor(color) {
      return frameInfos.some(function (fi) { return fi.printOnly && fi.color === color; });
    }

    function frameValueFor(color, type) {
      for (var i = 0; i < frameInfos.length; i++) {
        if (frameInfos[i].color === color && frameInfos[i].type === type) return frameInfos[i].value;
      }
      return null;
    }

    function typesAvailable(color, sizeVal) {
      return {
        classic: !!findVariant(sizeVal, frameValueFor(color, 'classic')),
        premium: !!findVariant(sizeVal, frameValueFor(color, 'premium'))
      };
    }

    function colorAvailable(color, sizeVal) {
      if (isPrintOnlyColor(color)) return !!findVariant(sizeVal, frameValueFor(color, 'none'));
      var t = typesAvailable(color, sizeVal);
      return t.classic || t.premium;
    }

    // Pick the frame value to use when a size lacks the currently selected
    // frame: keep the current colour+type if possible, else swap Classic<->Box
    // for the same colour, else fall back to Print Only, else the first frame
    // that exists for the size. This guarantees we never land on a missing
    // (Unavailable) variant — print-only-only sizes resolve to Print Only.
    function frameForSize(sizeVal, cur) {
      if (findVariant(sizeVal, cur.value)) return cur.value;
      if (!cur.printOnly) {
        var alt = frameValueFor(cur.color, cur.type === 'premium' ? 'classic' : 'premium');
        if (alt && findVariant(sizeVal, alt)) return alt;
      }
      for (var i = 0; i < frameInfos.length; i++) {
        if (frameInfos[i].printOnly && findVariant(sizeVal, frameInfos[i].value)) return frameInfos[i].value;
      }
      for (var j = 0; j < frameInfos.length; j++) {
        if (findVariant(sizeVal, frameInfos[j].value)) return frameInfos[j].value;
      }
      return null;
    }

    // sizes in variant order
    var sizes = [];
    (function () {
      var seen = {};
      variants.forEach(function (v) {
        var sv = sizePos ? v[optKey(sizePos)] : null;
        if (sv != null && !seen[sv]) { seen[sv] = 1; sizes.push(sv); }
      });
    })();

    function setSelect(select, value) {
      if (!select || value == null) return false;
      if (select.value === value) return false;
      var exists = Array.prototype.some.call(select.options, function (o) { return o.value === value; });
      if (!exists) return false;
      select.value = value;
      select.dispatchEvent(new Event('change', { bubbles: true }));
      return true;
    }

    // ---- build DOM ------------------------------------------------------------
    sizes.forEach(function (sizeVal) {
      var row = document.createElement('button');
      row.type = 'button';
      row.className = 'fo-size-row';
      row.setAttribute('data-fo-size', sizeVal);
      row.innerHTML =
        '<span class="fo-size-row__name">' + sizeName(sizeVal) + '</span>' +
        '<span class="fo-size-row__dims" data-fo-dims></span>' +
        '<span class="fo-size-row__price" data-fo-price></span>' +
        '<span class="fo-size-row__chev" aria-hidden="true"></span>';
      row.addEventListener('click', function () {
        if (row.hasAttribute('disabled')) return;
        // Collapsed list (all viewports): the first click on the selected
        // summary row just opens the full list.
        if (!sizeRowsEl.classList.contains('fo-size--open')) {
          sizeRowsEl.classList.add('fo-size--open');
          return;
        }
        // If the current frame isn't sold in this size, fall back (Print Only
        // for print-only-only sizes) so we never select an Unavailable variant.
        var cur = parseFrame(frameSelect.value);
        var nextFrame = frameForSize(sizeVal, cur);
        setSelect(sizeSelect, sizeVal);
        if (nextFrame && nextFrame !== frameSelect.value) setSelect(frameSelect, nextFrame);
        sizeRowsEl.classList.remove('fo-size--open'); // collapse back after choosing
        sync();
        // Belt-and-suspenders: always re-assert the final frame to the theme
        // after a size pick, so its variant matcher settles on the corrected
        // combo even when the correction happened inside a nested dispatch.
        scheduleThemeSettle();
      });
      sizeRowsEl.appendChild(row);
    });

    colors.forEach(function (color) {
      var info = frameInfos.filter(function (fi) { return fi.color === color; })[0];
      var sw = document.createElement('button');
      sw.type = 'button';
      sw.className = 'fo-frame-swatch';
      sw.setAttribute('data-fo-color', color);
      var swatchLabel = info.printOnly ? 'Unframed' : color;
      sw.innerHTML =
        '<span class="fo-frame-swatch__tile">' +
          '<span class="fo-frame-swatch__chip fo-frame-swatch__chip--' + chipClass(color, info.printOnly) + '"></span>' +
        '</span>' +
        '<span class="fo-frame-swatch__label">' + swatchLabel + '</span>';
      sw.addEventListener('click', function () {
        if (sw.hasAttribute('disabled')) return;
        selectColor(color);
      });
      swatchesEl.appendChild(sw);
    });

    var typeDefs = [];
    if (hasClassic) typeDefs.push({ type: 'classic', label: classicLabel });
    if (hasPremium) typeDefs.push({ type: 'premium', label: premiumLabel });
    typeDefs.forEach(function (def) {
      var btn = document.createElement('button');
      btn.type = 'button';
      btn.className = 'fo-type-btn';
      btn.setAttribute('data-fo-type-btn', def.type);
      btn.textContent = def.label;
      btn.addEventListener('click', function () {
        if (btn.hasAttribute('hidden')) return;
        selectType(def.type);
      });
      typeBtnsEl.appendChild(btn);
    });

    // ---- selection actions ----------------------------------------------------
    function selectColor(color) {
      var curSize = sizeSelect ? sizeSelect.value : null;
      if (isPrintOnlyColor(color)) {
        setSelect(frameSelect, frameValueFor(color, 'none'));
        sync();
        return;
      }
      var cur = parseFrame(frameSelect.value);
      var avail = typesAvailable(color, curSize);
      var wantType = cur.type === 'premium' && avail.premium ? 'premium'
        : cur.type === 'classic' && avail.classic ? 'classic'
        : avail.classic ? 'classic' : (avail.premium ? 'premium' : null);
      if (!wantType) { sync(); return; }
      setSelect(frameSelect, frameValueFor(color, wantType));
      sync();
    }

    function selectType(type) {
      var cur = parseFrame(frameSelect.value);
      if (cur.printOnly) { sync(); return; }
      var curSize = sizeSelect ? sizeSelect.value : null;
      var value = frameValueFor(cur.color, type);
      if (!findVariant(curSize, value)) { sync(); return; }
      setSelect(frameSelect, value);
      sync();
    }

    // ---- render / sync --------------------------------------------------------
    // When we auto-correct the frame (e.g. Print-Only-only sizes like Epic /
    // Statement), the corrected frame's 'change' fires NESTED inside the size
    // 'change' that triggered it. The theme's variant matcher processes the size
    // change (landing on new-size + old-frame, a variant it doesn't have) but
    // never cleanly registers the corrected frame, so the buy button sticks on
    // "Unavailable" even though the selects now hold a valid, in-stock variant.
    // The theme's option1 is already correct (it saw the size change); it just
    // needs to re-read the frame. Re-fire a single clean, non-nested 'change' on
    // the FRAME select after the stack unwinds and the theme settles on the
    // final size+frame. (Re-firing the size change too re-tangles it, so we
    // deliberately dispatch only the frame.) Debounced to avoid stacking timers;
    // the re-fire never self-corrects again, so there is no loop.
    var themeSettleQueued = false;
    function scheduleThemeSettle() {
      if (themeSettleQueued) return;
      themeSettleQueued = true;
      setTimeout(function () {
        themeSettleQueued = false;
        if (frameSelect) frameSelect.dispatchEvent(new Event('change', { bubbles: true }));
      }, 0);
    }

    // Directly update the cart-relevant DOM for the given variant: the form's
    // master variant select/input (what actually gets submitted to /cart/add)
    // and .price-list (the theme's own price display, which atc-price.js
    // already watches to keep the Add to Cart button price in sync). See the
    // call site in sync() for why this can't be left to the theme itself.
    function updateCartState(variant) {
      if (!variant) return;

      var master = root.querySelector('[name="id"]');
      if (master && String(master.value) !== String(variant.id)) {
        master.value = String(variant.id);
      }

      var priceList = root.querySelector('.price-list');
      if (priceList) {
        var html = variant.compare_at_price > variant.price
          ? '<span class="price price--highlight">' + formatMoney(variant.price) + '</span>' +
            '<span class="price price--compare">' + formatMoney(variant.compare_at_price) + '</span>'
          : '<span class="price">' + formatMoney(variant.price) + '</span>';
        if (priceList.innerHTML !== html) priceList.innerHTML = html;
      }
    }

    function sync() {
      var curSize = sizeSelect ? sizeSelect.value : null;
      var cur = parseFrame(frameSelect.value);

      // Self-correct: if the current size + frame has no variant (e.g. a size
      // that is Print-Only-only while a Classic/Premium frame is selected),
      // switch to a valid frame so we never sit on an "Unavailable" variant.
      // Runs on EVERY sync, so it works no matter how the size was changed
      // (our size rows, the native select, or the theme's own variant scripts).
      if (curSize != null && !findVariant(curSize, cur.value)) {
        var corrected = frameForSize(curSize, cur);
        if (corrected && corrected !== cur.value && setSelect(frameSelect, corrected)) {
          scheduleThemeSettle();
          return; // setSelect dispatched 'change' -> sync() will run again
        }
      }

      // Keep the cart wired to whatever is actually selected. The theme's own
      // variant-change handler (theme.min.js _onOptionChanged, bound via
      // ftlabs-delegate on the section) does not fire for this product's
      // Size/Frame selects — confirmed by testing a plain, non-scripted
      // native <select> change in the browser: nothing downstream reacts.
      // The likely cause is the "zero-price variant filter" inline script in
      // product-info.liquid, which attaches its OWN 'change' listener
      // directly on these same selects and rebuilds their <option> children
      // (select.innerHTML = '') synchronously inside that same dispatch,
      // which stops the event from ever reaching the theme's delegated
      // ancestor listener. Net effect: .price-list, the Add to Cart price
      // (atc-price.js mirrors .price-list, so fixing this also fixes that),
      // and the cart form's master <select name="id"> all stay stuck on the
      // page-load variant no matter what is picked. We do not touch the
      // zero-price filter or theme.min.js (shared, fragile); we just update
      // the cart-relevant DOM ourselves, the same way this file already
      // patches around other theme quirks.
      updateCartState(findVariant(curSize, cur.value));

      var curColor = cur.color;
      var curType = cur.type;
      var curBorderType = cur.printOnly ? 'none' : curType;

      // Selected size name in the section header ("Size: Small").
      if (sizeValueEl) sizeValueEl.textContent = curSize != null ? sizeName(curSize) : '';

      // Size rows: dims for current frame + price + selected/disabled.
      Array.prototype.forEach.call(sizeRowsEl.children, function (row) {
        var sizeVal = row.getAttribute('data-fo-size');
        var dimsEl = row.querySelector('[data-fo-dims]');
        var priceEl = row.querySelector('[data-fo-price]');

        if (dimsEl) {
          dimsEl.textContent = formatDims(sizeVal, curBorderType);
        }

        // Price for the EXACT current frame. If this size isn't sold with the
        // selected frame (e.g. Premium on a Print-Only-only size), show "N/A"
        // rather than falling back to a different frame's price.
        var variant = findVariant(sizeVal, cur.value);
        if (priceEl) priceEl.textContent = variant ? formatMoney(variant.price) : 'N/A';

        var anyForSize = variants.some(function (v) { return !sizePos || v[optKey(sizePos)] === sizeVal; });
        if (anyForSize) { row.removeAttribute('disabled'); } else { row.setAttribute('disabled', ''); }
        row.setAttribute('aria-checked', sizeVal === curSize ? 'true' : 'false');
      });

      // Frame colour swatches.
      if (frameColorLabel) frameColorLabel.textContent = curColor || '';
      Array.prototype.forEach.call(swatchesEl.children, function (sw) {
        var color = sw.getAttribute('data-fo-color');
        if (colorAvailable(color, curSize)) { sw.removeAttribute('disabled'); }
        else { sw.setAttribute('disabled', ''); }
        sw.setAttribute('aria-checked', color === curColor ? 'true' : 'false');
      });

      // Framing type toggle: hide entirely for Print Only; otherwise show
      // available types, hide a type with no variant for this size+colour.
      if (cur.printOnly || (!hasClassic && !hasPremium)) {
        if (typeEl) typeEl.setAttribute('hidden', '');
      } else {
        if (typeEl) typeEl.removeAttribute('hidden');
        var avail = typesAvailable(curColor, curSize);

        Array.prototype.forEach.call(typeBtnsEl.children, function (btn) {
          var t = btn.getAttribute('data-fo-type-btn');
          var ok = t === 'classic' ? avail.classic : avail.premium;
          if (ok) { btn.removeAttribute('hidden'); } else { btn.setAttribute('hidden', ''); }
          btn.setAttribute('aria-checked', t === curType ? 'true' : 'false');
        });
        if (typeValueLabel) {
          typeValueLabel.textContent = curType === 'premium' ? premiumLabel : (curType === 'classic' ? classicLabel : '');
        }
      }

      // Per-market Shadow Box note: only when Premium selected and note non-blank.
      if (auNoteEl) {
        var noteText = (auNoteEl.textContent || '').trim();
        if (curType === 'premium' && noteText) { auNoteEl.removeAttribute('hidden'); }
        else { auNoteEl.setAttribute('hidden', ''); }
      }
    }

    // White Border: replace the native <select> with Yes/No buttons (same look
    // as the framing type toggle). The select stays in the DOM as source of
    // truth for framing.js and the cart; buttons proxy onto it.
    (function buildWhiteBorder() {
      var field = root.querySelector('.native-white-border-field');
      if (!field || field.__foWb) return;
      var sel = field.querySelector('#native-white-border-select') || field.querySelector('select');
      if (!sel || !sel.options.length) return;
      field.__foWb = true;

      var label = field.querySelector('#wb-selected-label');
      var group = document.createElement('div');
      group.className = 'fo-wb__buttons';

      // Show "Yes" first regardless of the select's option order.
      var opts = Array.prototype.slice.call(sel.options).sort(function (a, b) {
        var ay = /^yes$/i.test(a.value) ? 0 : 1;
        var by = /^yes$/i.test(b.value) ? 0 : 1;
        return ay - by;
      });
      opts.forEach(function (opt) {
        var btn = document.createElement('button');
        btn.type = 'button';
        btn.className = 'fo-wb-btn';
        btn.setAttribute('data-fo-wb', opt.value);
        btn.textContent = opt.text || opt.value;
        btn.addEventListener('click', function () {
          if (sel.value !== opt.value) {
            sel.value = opt.value;
            sel.dispatchEvent(new Event('change', { bubbles: true }));
          }
          syncWb();
        });
        group.appendChild(btn);
      });

      var wrap = field.querySelector('.select-wrapper');
      if (wrap && wrap.parentNode) { wrap.parentNode.insertBefore(group, wrap.nextSibling); }
      else { field.appendChild(group); }
      field.classList.add('fo-wb-enhanced');

      function syncWb() {
        Array.prototype.forEach.call(group.children, function (b) {
          b.setAttribute('aria-checked', b.getAttribute('data-fo-wb') === sel.value ? 'true' : 'false');
        });
        if (label) label.textContent = sel.value;
      }
      sel.addEventListener('change', syncWb);
      syncWb();
    })();

    // Size Guide modal: trigger in the size header opens a server-rendered
    // modal (content filled by the selected page in the theme editor).
    (function buildSizeGuide() {
      var trigger = fo.querySelector('[data-fo-size-guide]');
      var modal = fo.querySelector('[data-fo-size-guide-modal]');
      if (!trigger || !modal) return;
      var lastFocus = null;

      function open() {
        lastFocus = document.activeElement;
        modal.removeAttribute('hidden');
        document.documentElement.style.overflow = 'hidden';
        var closeBtn = modal.querySelector('[data-fo-modal-close]');
        if (closeBtn && closeBtn.focus) closeBtn.focus();
      }
      function close() {
        modal.setAttribute('hidden', '');
        document.documentElement.style.overflow = '';
        if (lastFocus && lastFocus.focus) lastFocus.focus();
      }

      trigger.addEventListener('click', open);
      Array.prototype.forEach.call(modal.querySelectorAll('[data-fo-modal-close]'), function (el) {
        el.addEventListener('click', close);
      });
      document.addEventListener('keydown', function (e) {
        if (e.key === 'Escape' && !modal.hasAttribute('hidden')) close();
      });
    })();

    // Reveal the controls now that they are populated.
    if (frameEl) frameEl.removeAttribute('hidden');

    // Re-sync whenever the native selects change (covers our proxy, the
    // cascading zero-price filter and theme variant scripts).
    [sizeSelect, frameSelect].forEach(function (sel) {
      if (sel) sel.addEventListener('change', sync);
    });

    // ---- Default selection on a fresh landing: the store's preferred starting
    //      point — Medium size, Natural colour, Classic (non-box) frame. Only
    //      when the shopper hasn't deep-linked a specific variant (?variant=),
    //      so shared/returning links are respected. Missing options are skipped.
    //      Set size first, then frame, so the frame choice isn't auto-swapped by
    //      the size change. setSelect no-ops when the value already matches. ----
    if (location.search.indexOf('variant=') < 0) {
      var foWantSize = null;
      if (sizeSelect) Array.prototype.forEach.call(sizeSelect.options, function (o) {
        if (!foWantSize && o.value && /medium/i.test(o.value)) foWantSize = o.value;
      });
      if (foWantSize) setSelect(sizeSelect, foWantSize);
      var foHasNatural = Array.prototype.some.call(frameSelect.options, function (o) { return o.value === 'Natural'; });
      if (foHasNatural) setSelect(frameSelect, 'Natural');
    }

    sync();
  }

  // framing.js crash guard. Its Print-Only branch does, in effect:
  //   FrameImgContainer["Natural_frame"].find(size matches current).relative_ratio
  // and throws "Cannot read properties of undefined (reading 'relative_ratio')"
  // when the current size has no Natural-frame overlay entry — the case for
  // print-only-only sizes such as Epic / Statement (they carry no frame config).
  // Because framing.js shares the jQuery change-event dispatch with the theme's
  // variant selector, that throw ABORTS the theme's own variant handler, so the
  // buy button gets stuck on "Unavailable" even though our layer already
  // corrected size+frame to a valid, in-stock variant.
  //
  // We do NOT modify framing.js. We only backfill FrameImgContainer["Natural_frame"]
  // so every size present on the product resolves to a (fallback) entry and the
  // lookup can never return undefined. Fallbacks are appended AFTER the real
  // entries, so a genuine per-size match is always found first; a fallback is
  // used only for sizes that would otherwise crash.
  function guardFrameImgContainer(root, attempt) {
    var fic = window.FrameImgContainer;
    var arr = fic && fic.Natural_frame;
    if (!arr || !arr.length) {
      // Populated by inline section scripts; retry briefly if not ready yet.
      if (attempt < 10) setTimeout(function () { guardFrameImgContainer(root, attempt + 1); }, 200);
      return;
    }
    var sizeSelect = root.querySelector('.product-form__option.Size select, select[data-select-name="Size"]');
    if (!sizeSelect) return;
    var fallback = arr[arr.length - 1];
    Array.prototype.forEach.call(sizeSelect.options, function (opt) {
      var sizeVal = opt.value;
      if (!sizeVal) return;
      var has = arr.some(function (el) {
        return el.size_handle && sizeVal.indexOf(el.size_handle) !== -1;
      });
      if (!has) {
        arr.push({
          size_handle: sizeVal,
          size_url: fallback.size_url,
          padding_ratio: fallback.padding_ratio,
          relative_ratio: fallback.relative_ratio
        });
      }
    });
  }

  function boot() {
    document.querySelectorAll('.fo-variants').forEach(function (fo) {
      var section = fo.closest('[data-section-type="product"]')
        || fo.closest('.shopify-section')
        || document;
      guardFrameImgContainer(section, 0);
      init(section);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }
})();
